import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useBrand } from "../context/BrandContext";
import { API_BASE as API } from "../api/base";
import { login as loginApi } from "../api/auth";
import { marketplaceSignup } from "../api/marketplaceAuth";
import { notifyOnboardingStatusChange } from "../utils/onboarding";

const OPS_LOGIN_ERROR_MESSAGES = {
  USER_NOT_IN_SALON:
    "Your request is still waiting for approval. Refresh again after the salon owner or manager approves it.",
  SALON_ACCESS_INACTIVE:
    "Your salon access is still inactive. Ask the salon owner or manager to reactivate it.",
};

function buildOpsVerifyEmailUrl(email, salonId) {
  const params = new URLSearchParams();
  if (String(email || "").includes("@")) {
    params.set("email", String(email || "").trim());
  }
  if (String(salonId || "").trim()) {
    params.set("salonId", String(salonId || "").trim());
  }
  const suffix = params.toString() ? `?${params.toString()}` : "";
  return `/verify-email${suffix}`;
}

function formatIndependentProfessionalId(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const match = raw.match(/^IP-?0*(\d+)$/i);
  if (match) return match[1] || "0";
  return raw;
}

function getIndependentProfessionalDisplayId(successInfo) {
  const explicitNumber = successInfo?.independent_operator_number;
  if (explicitNumber !== undefined && explicitNumber !== null && explicitNumber !== "") {
    return String(explicitNumber);
  }
  return formatIndependentProfessionalId(successInfo?.independent_operator_id);
}

function buildOpsRegisterUrl(brand) {
  const configuredHost = String(brand?.opsHost || "").trim();
  if (configuredHost) {
    return `https://${configuredHost.replace(/^https?:\/\//, "").replace(/\/.*$/, "")}/register`;
  }
  try {
    const loginUrl = new URL(brand?.opsLoginUrl || "https://ellyanna.com/login");
    loginUrl.pathname = "/register";
    loginUrl.search = "";
    loginUrl.hash = "";
    return loginUrl.toString();
  } catch {
    return "https://ellyanna.com/register";
  }
}

function getInitialMarketplaceRegistrationPath(search = "") {
  const params = new URLSearchParams(search || "");
  const role = String(params.get("role") || "").trim().toLowerCase();
  if (role === "host" || role === "salon_owner" || role === "owner") {
    return "salon_owner";
  }
  return "independent_professional";
}

function getInitialOpsAccountType(search = "") {
  const params = new URLSearchParams(search || "");
  const role = String(params.get("role") || "").trim().toLowerCase();
  if (role === "independent_professional") {
    return "independent_professional";
  }
  return "owner";
}

function getInitialOpsRegistrationPath(search = "") {
  const params = new URLSearchParams(search || "");
  const role = String(params.get("role") || "").trim().toLowerCase();
  if (role === "independent_professional") {
    return "independent_professional";
  }
  if (role === "existing" || role === "technician" || role === "staff") {
    return "existing";
  }
  if (role === "host" || role === "salon_owner" || role === "owner") {
    return "owner";
  }
  return "";
}

function MarketplaceRegister({
  error,
  fieldErrors,
  form,
  loading,
  onChange,
  opsRegisterUrl,
  opsLoginUrl,
  onSubmit,
  selectedRegistrationPath,
  setSelectedRegistrationPath,
}) {
  const inputClassName =
    "w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-rose-300 focus:border-rose-400";
  const labelClassName = "mb-1 block text-xs font-medium uppercase tracking-wide text-slate-700";
  const helperClassName = "mt-1 text-xs text-rose-600";
  const mutedHelperClassName = "mt-1 text-xs leading-5 text-slate-500";
  const primaryButtonClass =
    "inline-flex min-h-11 items-center justify-center rounded-xl bg-rose-500 px-5 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-rose-600 disabled:cursor-not-allowed disabled:opacity-60";
  const secondaryButtonClass =
    "inline-flex min-h-11 items-center justify-center rounded-xl border border-slate-300 bg-white px-5 py-3 text-sm font-semibold text-slate-900 transition hover:border-slate-400 hover:bg-slate-50";
  const pathButtonClass = (active) =>
    `rounded-lg px-3 py-2 text-sm font-semibold transition ${
      active
        ? "bg-white text-slate-900 shadow-sm"
        : "text-slate-600 hover:text-slate-900"
    }`;
  const isIndependentPath = selectedRegistrationPath === "independent_professional";

  return (
    <div className="bg-[radial-gradient(circle_at_top,_#fef3f2,_#f7ebe7_42%,_#eef2ff_100%)] px-4 py-8 md:px-6 lg:py-10">
      <div className="mx-auto w-full max-w-6xl space-y-8 text-sm md:text-base">
        <section className="rounded-[28px] border border-white/70 bg-white/80 p-6 shadow-[0_20px_70px_rgba(47,53,102,0.12)] backdrop-blur sm:p-8">
          <p className="inline-flex rounded-full border border-rose-200 bg-rose-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-rose-600">
            Ellyanna MyBoothShare
          </p>
          <div className="mt-5 grid gap-6 lg:grid-cols-[1fr,0.9fr] lg:items-end">
            <div>
              <h1 className="max-w-3xl text-4xl font-semibold tracking-tight text-slate-900 md:text-5xl md:leading-tight">
                Create the right account for how you work.
              </h1>
              <p className="mt-4 max-w-3xl text-base leading-7 text-slate-600">
                Choose the path that fits you today. Independent professionals
                can create their account here. Salon owners start in Ellyanna
                Booking, then use MyBoothShare to list workspace after salon
                setup.
              </p>
            </div>
            <div>
              <div className="mb-2 text-xs font-semibold uppercase tracking-[0.18em] text-slate-700">
                Choose your path
              </div>
              <div className="grid grid-cols-2 gap-1 rounded-xl bg-slate-100 p-1">
                <button
                  type="button"
                  aria-pressed={isIndependentPath}
                  onClick={() => setSelectedRegistrationPath("independent_professional")}
                  className={pathButtonClass(isIndependentPath)}
                >
                  Independent Professional
                </button>
                <button
                  type="button"
                  aria-pressed={!isIndependentPath}
                  onClick={() => setSelectedRegistrationPath("salon_owner")}
                  className={pathButtonClass(!isIndependentPath)}
                >
                  Salon Owner
                </button>
              </div>
            </div>
          </div>
        </section>

        {isIndependentPath ? (
        <div className="grid gap-8 lg:grid-cols-[1.05fr,0.95fr] lg:items-start">
          <section className="rounded-[28px] border border-white/70 bg-white/80 p-6 shadow-[0_20px_70px_rgba(47,53,102,0.12)] backdrop-blur sm:p-8">
            <h2 className="max-w-xl text-3xl font-semibold tracking-tight text-slate-900 md:text-4xl md:leading-tight">
              Create your Independent Professional account.
            </h2>
            <p className="mt-4 max-w-2xl text-base leading-7 text-slate-600">
              Start your MyBoothShare account and get the Ellyanna Booking tools
              you need to manage your clients, services, schedule, and workspace
              bookings.
            </p>
            <div className="mt-6 grid gap-3 sm:grid-cols-2">
              <div className="rounded-2xl border border-slate-200 bg-white px-4 py-4 shadow-sm">
                <div className="text-sm font-semibold text-slate-900">
                  Find workspace opportunities
                </div>
                <p className="mt-1 text-sm leading-6 text-slate-600">
                  Search for salon workspaces, review rental details, and manage
                  your booking requests in one place.
                </p>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white px-4 py-4 shadow-sm">
                <div className="text-sm font-semibold text-slate-900">
                  Run your client business
                </div>
                <p className="mt-1 text-sm leading-6 text-slate-600">
                  Use Ellyanna Booking to manage your services, clients,
                  schedule, and business setup as an independent professional.
                </p>
              </div>
            </div>
          </section>

          <section className="rounded-[28px] border border-slate-200 bg-white px-5 py-5 shadow-[0_20px_60px_rgba(15,23,42,0.12)] md:px-7 md:py-7">
            <div className="space-y-5 px-4 py-5 sm:px-6 sm:py-6">
              <div>
                <h2 className="text-2xl font-semibold text-slate-900">
                  Create your account
                </h2>
                <p className="mt-2 text-sm leading-6 text-slate-600">
                  Use this account for MyBoothShare workspace rentals and your
                  Ellyanna Booking business tools.
                </p>
              </div>
              {error ? (
                <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                  {error}
                </div>
              ) : null}

              <form onSubmit={onSubmit} className="space-y-4">
                <div className="text-xs font-semibold uppercase tracking-[0.18em] text-[#2f3566]">
                  Independent Professional details
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label htmlFor="marketplace-register-first-name" className={labelClassName}>
                      First name
                    </label>
                    <input
                      id="marketplace-register-first-name"
                      type="text"
                      className={inputClassName}
                      value={form.firstName}
                      onChange={(e) => onChange("firstName", e.target.value)}
                      placeholder="First name"
                      required
                    />
                    {fieldErrors.first_name ? (
                      <p className={helperClassName}>{fieldErrors.first_name}</p>
                    ) : null}
                  </div>
                  <div>
                    <label htmlFor="marketplace-register-last-name" className={labelClassName}>
                      Last name
                    </label>
                    <input
                      id="marketplace-register-last-name"
                      type="text"
                      className={inputClassName}
                      value={form.lastName}
                      onChange={(e) => onChange("lastName", e.target.value)}
                      placeholder="Last name"
                      required
                    />
                    {fieldErrors.last_name ? (
                      <p className={helperClassName}>{fieldErrors.last_name}</p>
                    ) : null}
                  </div>
                </div>

                <div>
                  <label htmlFor="marketplace-register-email" className={labelClassName}>
                    Email
                  </label>
                  <input
                    id="marketplace-register-email"
                    type="email"
                    className={inputClassName}
                    value={form.email}
                    onChange={(e) => onChange("email", e.target.value)}
                    placeholder="you@example.com"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="marketplace-register-username" className={labelClassName}>
                    Login username
                  </label>
                  <input
                    id="marketplace-register-username"
                    type="text"
                    className={inputClassName}
                    value={form.username}
                    onChange={(e) => onChange("username", e.target.value)}
                    placeholder="Optional username"
                  />
                  <p className="mt-1 text-xs text-slate-500">
                    Optional. You can sign in later with your email or username.
                  </p>
                  {fieldErrors.username ? (
                    <p className={helperClassName}>{fieldErrors.username}</p>
                  ) : null}
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label htmlFor="marketplace-register-password" className={labelClassName}>
                      Password
                    </label>
                    <input
                      id="marketplace-register-password"
                      type="password"
                      className={inputClassName}
                      value={form.password}
                      onChange={(e) => onChange("password", e.target.value)}
                      placeholder="At least 8 characters"
                      required
                    />
                    {fieldErrors.password ? (
                      <p className={helperClassName}>{fieldErrors.password}</p>
                    ) : null}
                  </div>
                  <div>
                    <label
                      htmlFor="marketplace-register-confirm-password"
                      className={labelClassName}
                    >
                      Confirm password
                    </label>
                    <input
                      id="marketplace-register-confirm-password"
                      type="password"
                      className={inputClassName}
                      value={form.confirmPassword}
                      onChange={(e) => onChange("confirmPassword", e.target.value)}
                      placeholder="Re-enter password"
                      required
                    />
                    {fieldErrors.confirm_password ? (
                      <p className={helperClassName}>{fieldErrors.confirm_password}</p>
                    ) : null}
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label htmlFor="marketplace-register-display-name" className={labelClassName}>
                      Display name
                    </label>
                    <input
                      id="marketplace-register-display-name"
                      type="text"
                      className={inputClassName}
                      value={form.displayName}
                      onChange={(e) => onChange("displayName", e.target.value)}
                      placeholder="Professional profile name"
                    />
                    <p className={mutedHelperClassName}>
                      This is the name shown on your professional profile.
                    </p>
                  </div>
                  <div>
                    <label htmlFor="marketplace-register-phone" className={labelClassName}>
                      Phone
                    </label>
                    <input
                      id="marketplace-register-phone"
                      type="text"
                      className={inputClassName}
                      value={form.phone}
                      onChange={(e) => onChange("phone", e.target.value)}
                      placeholder="Optional phone"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label htmlFor="marketplace-register-business-name" className={labelClassName}>
                      Business name
                    </label>
                    <input
                      id="marketplace-register-business-name"
                      type="text"
                      className={inputClassName}
                      value={form.businessName}
                      onChange={(e) => onChange("businessName", e.target.value)}
                      placeholder="Optional business name"
                    />
                    <p className={mutedHelperClassName}>
                      Optional. Use your professional or business name.
                    </p>
                  </div>
                  <div>
                    <label htmlFor="marketplace-register-license-type" className={labelClassName}>
                      License type
                    </label>
                    <input
                      id="marketplace-register-license-type"
                      type="text"
                      className={inputClassName}
                      value={form.licenseType}
                      onChange={(e) => onChange("licenseType", e.target.value)}
                      placeholder="Optional license or certification"
                    />
                    <p className={mutedHelperClassName}>
                      Optional. Add your license or certification type if
                      applicable.
                    </p>
                  </div>
                </div>

                <div>
                  <label htmlFor="marketplace-register-instagram" className={labelClassName}>
                    Instagram
                  </label>
                  <input
                    id="marketplace-register-instagram"
                    type="text"
                    className={inputClassName}
                    value={form.instagramHandle}
                    onChange={(e) => onChange("instagramHandle", e.target.value)}
                    placeholder="@yourhandle"
                  />
                </div>

                <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 text-sm leading-6 text-slate-600">
                  <div className="font-semibold text-slate-900">
                    Your independent professional setup
                  </div>
                  <p className="mt-1">
                    Your account is designed for professionals who manage their
                    own clients and business activity while using MyBoothShare to
                    find and manage workspace bookings.
                  </p>
                </div>

                <label className="flex items-start gap-3 rounded-2xl border border-[#2f3566]/15 bg-[#2f3566]/5 px-4 py-4 text-sm leading-6 text-slate-700">
                  <input
                    type="checkbox"
                    className="mt-1 h-4 w-4 rounded border-slate-300 text-rose-500 focus:ring-rose-400"
                    checked={Boolean(form.ellyannaBookingRequiredAccepted)}
                    onChange={(e) =>
                      onChange(
                        "ellyannaBookingRequiredAccepted",
                        e.target.checked
                      )
                    }
                    required
                  />
                  <span>
                    I understand that MyBoothShare includes Ellyanna Booking
                    access so I can manage my clients, services, schedule, and
                    workspace bookings.
                  </span>
                </label>
                {fieldErrors.ellyanna_booking_required ? (
                  <p className={helperClassName}>
                    {fieldErrors.ellyanna_booking_required}
                  </p>
                ) : null}

                <button
                  type="submit"
                  disabled={loading}
                  className={`w-full ${primaryButtonClass}`}
                >
                  {loading ? "Creating your account..." : "Create account"}
                </button>
              </form>
              <p className="text-center text-sm text-slate-600">
                Already have an account?{" "}
                <Link to="/login" className="font-semibold text-rose-600 hover:text-rose-700">
                  Sign in
                </Link>
              </p>
            </div>
          </section>
        </div>
        ) : (
        <div className="grid gap-8 lg:grid-cols-[1.05fr,0.95fr] lg:items-start">
          <section className="rounded-[28px] border border-white/70 bg-white/80 p-6 shadow-[0_20px_70px_rgba(47,53,102,0.12)] backdrop-blur sm:p-8">
            <h2 className="max-w-xl text-3xl font-semibold tracking-tight text-slate-900 md:text-4xl md:leading-tight">
              Register your salon in Ellyanna Booking first.
            </h2>
            <p className="mt-4 max-w-2xl text-base leading-7 text-slate-600">
              Salon owners start in Ellyanna Booking because your salon profile,
              staff, services, schedule, billing, and business setup need to be
              created before you list workspace on MyBoothShare.
            </p>
            <div className="mt-6 grid gap-3">
              <div className="rounded-2xl border border-slate-200 bg-white px-4 py-4 shadow-sm">
                <div className="text-sm font-semibold text-slate-900">
                  Set up your salon foundation
                </div>
                <p className="mt-1 text-sm leading-6 text-slate-600">
                  Create your salon account, add your business details,
                  services, hours, and billing in Ellyanna Booking.
                </p>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white px-4 py-4 shadow-sm">
                <div className="text-sm font-semibold text-slate-900">
                  List workspace after setup
                </div>
                <p className="mt-1 text-sm leading-6 text-slate-600">
                  Once your salon is ready, open MyBoothShare to list chairs,
                  rooms, or stations and manage workspace rental requests.
                </p>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white px-4 py-4 shadow-sm">
                <div className="text-sm font-semibold text-slate-900">
                  Keep your salon organized
                </div>
                <p className="mt-1 text-sm leading-6 text-slate-600">
                  Use Ellyanna Booking for salon operations and MyBoothShare for
                  workspace rentals, so each part of your business has the right
                  place.
                </p>
              </div>
            </div>
          </section>

          <section className="rounded-[28px] border border-slate-200 bg-white px-5 py-5 shadow-[0_20px_60px_rgba(15,23,42,0.12)] md:px-7 md:py-7">
            <div className="space-y-5 px-4 py-5 sm:px-6 sm:py-6">
              <div>
                <h2 className="text-2xl font-semibold text-slate-900">
                  For salon owners
                </h2>
                <p className="mt-2 text-sm leading-6 text-slate-600">
                  To list workspace on MyBoothShare, first create your salon
                  account in Ellyanna Booking. After setup, you can return to
                  MyBoothShare and use List Space to manage your rental listings.
                </p>
              </div>
              <div className="flex flex-col gap-3">
                <a href={opsRegisterUrl} className={primaryButtonClass}>
                  Register your salon in Ellyanna Booking
                </a>
                <a href={opsLoginUrl} className={secondaryButtonClass}>
                  I already have an Ellyanna account
                </a>
              </div>
              <button
                type="button"
                onClick={() => setSelectedRegistrationPath("independent_professional")}
                className="text-left text-sm font-semibold text-rose-600 hover:text-rose-700"
              >
                Looking for workspace instead? Choose Independent Professional.
              </button>
            </div>
          </section>
        </div>
        )}
      </div>
    </div>
  );
}

function OpsRegister({
  accountType,
  autoLoginLoading,
  clearFieldError,
  confirmPassword,
  email,
  error,
  existingSalonId,
  fieldErrors,
  firstName,
  handleAutoLogin,
  handleRefreshAccess,
  handleModeChange,
  handleSubmit,
  lastName,
  loading,
  mode,
  navigate,
  password,
  refreshStatusMessage,
  salonName,
  selectedAccountPath,
  setSelectedAccountPath,
  setConfirmPassword,
  setEmail,
  setExistingSalonId,
  setFirstName,
  setLastName,
  setPassword,
  setAccountType,
  setUsername,
  setSalonName,
  successInfo,
  username,
}) {
  const inputClassName =
    "w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-rose-300 focus:border-rose-400";
  const labelClassName =
    "mb-1 block text-xs font-medium uppercase tracking-wide text-slate-700";
  const helperClassName = "mt-1 text-xs text-rose-600";
  const pathButtonClass = (active) =>
    `group flex gap-4 rounded-2xl border px-5 py-5 text-left transition ${
      active
        ? "border-[#2f3566] bg-[#2f3566] text-white shadow-[0_18px_50px_rgba(47,53,102,0.28)] ring-2 ring-[#2f3566]/20"
        : "border-slate-300 bg-white text-slate-700 shadow-sm hover:-translate-y-0.5 hover:border-[#2f3566]/45 hover:bg-white hover:shadow-[0_16px_40px_rgba(47,53,102,0.14)]"
    }`;
  const pathMarkerClass = (active) =>
    `flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl text-sm font-bold transition ${
      active
        ? "bg-white text-[#2f3566]"
        : "bg-rose-50 text-rose-500 group-hover:bg-[#2f3566] group-hover:text-white"
    }`;
  const isIndependentProfessional =
    accountType === "independent_professional";
  const hasSelectedAccountPath = Boolean(selectedAccountPath);
  const formIntro =
    mode === "existing"
      ? "Use this option if a salon already gave you a Salon ID and you need access to that salon's Ellyanna Booking account."
      : isIndependentProfessional
      ? "Create your account for your own clients, services, schedule, business setup, workspace rentals, and SalonBusinessCPA records."
      : "Create your account for your salon business, team, services, clients, setup, MyBoothShare listings, and SalonBusinessCPA records.";
  const businessNameLabel = isIndependentProfessional
    ? "Professional or business name"
    : "Salon business name";
  const businessNamePlaceholder = isIndependentProfessional
    ? "Your professional or business name"
    : "Your salon business name";
  const formTitle =
    mode === "existing"
      ? "Join an existing salon"
      : isIndependentProfessional
      ? "Create your independent professional account"
      : "Create your salon owner account";
  const submitLabel =
    mode === "existing"
      ? "Request access"
      : isIndependentProfessional
      ? "Create independent professional account"
      : "Create salon owner account";
  const loadingLabel =
    mode === "existing" ? "Sending request..." : "Creating your account...";
  const chooseOwner = () => {
    setSelectedAccountPath("owner");
    handleModeChange("new");
    setAccountType("owner");
  };
  const chooseIndependentProfessional = () => {
    setSelectedAccountPath("independent_professional");
    handleModeChange("new");
    setAccountType("independent_professional");
  };
  const chooseExistingSalon = () => {
    setSelectedAccountPath("existing");
    handleModeChange("existing");
  };

  return (
    <div className="bg-[radial-gradient(circle_at_top,_#fef3f2,_#f7ebe7_42%,_#eef2ff_100%)] px-4 py-8 md:px-6 lg:py-10">
      <div className="mx-auto grid w-full max-w-6xl gap-8 text-sm md:text-base lg:grid-cols-[1.05fr,0.95fr] lg:items-start">
        <section className="rounded-[28px] border border-white/70 bg-white/75 p-6 shadow-[0_20px_70px_rgba(47,53,102,0.12)] backdrop-blur sm:p-8">
          <p className="inline-flex rounded-full border border-rose-200 bg-rose-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-rose-600">
            ELLYANNA
          </p>
          <h1 className="mt-6 max-w-xl text-4xl font-semibold tracking-tight text-slate-900 md:text-5xl md:leading-tight">
            Create your Ellyanna account.
          </h1>
          <p className="mt-4 max-w-2xl text-base leading-7 text-slate-600">
            Start with the account type that matches how you work. Ellyanna
            Booking is the foundation for appointments, clients, services, and
            business setup. MyBoothShare and SalonBusinessCPA connect to your
            Ellyanna account when you need workspace rental tools, accounting,
            tax, payroll, bookkeeping, and document checklists.
          </p>

          {!successInfo ? (
            <div className="mt-7 rounded-[26px] border border-[#2f3566]/15 bg-gradient-to-br from-[#f7f8ff] via-white to-rose-50/70 p-4 shadow-[0_18px_55px_rgba(47,53,102,0.12)] sm:p-5">
              <div className="flex items-start gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-[#2f3566] text-sm font-bold text-white shadow-lg">
                  1
                </span>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#2f3566]">
                    Choose your account type first
                  </p>
                  <h2 className="mt-1 text-2xl font-semibold tracking-tight text-slate-950">
                    What kind of Ellyanna account do you need?
                  </h2>
                  <p className="mt-2 text-sm leading-6 text-slate-600">
                    Pick one option below. The registration form appears after
                    your choice so the right fields are shown.
                  </p>
                </div>
              </div>
              <div className="mt-3 grid gap-3">
                <button
                  type="button"
                  onClick={chooseOwner}
                  aria-label="Salon Owner"
                  aria-pressed={selectedAccountPath === "owner"}
                  className={pathButtonClass(selectedAccountPath === "owner")}
                >
                  <span className={pathMarkerClass(selectedAccountPath === "owner")}>A</span>
                  <span className="min-w-0">
                    <span className="block text-base font-semibold">Salon Owner</span>
                    <span className="mt-2 block text-sm leading-6 opacity-85">
                      Create an Ellyanna Booking account for a salon business with
                      clients, services, staff, schedules, sales, setup,
                      MyBoothShare listings, and SalonBusinessCPA.
                    </span>
                  </span>
                </button>
                <button
                  type="button"
                  onClick={chooseIndependentProfessional}
                  aria-label="Independent Professional"
                  aria-pressed={selectedAccountPath === "independent_professional"}
                  className={pathButtonClass(selectedAccountPath === "independent_professional")}
                >
                  <span className={pathMarkerClass(selectedAccountPath === "independent_professional")}>B</span>
                  <span className="min-w-0">
                    <span className="block text-base font-semibold">
                      Independent Professional
                    </span>
                    <span className="mt-2 block text-sm leading-6 opacity-85">
                      Create an account for yourself if you manage your own
                      clients, services, schedule, business setup, workspace
                      rentals, and SalonBusinessCPA records.
                    </span>
                  </span>
                </button>
                <button
                  type="button"
                  onClick={chooseExistingSalon}
                  aria-label="Join Existing Salon"
                  aria-pressed={selectedAccountPath === "existing"}
                  className={pathButtonClass(selectedAccountPath === "existing")}
                >
                  <span className={pathMarkerClass(selectedAccountPath === "existing")}>C</span>
                  <span className="min-w-0">
                    <span className="block text-base font-semibold">
                      Join Existing Salon
                    </span>
                    <span className="mt-2 block text-sm leading-6 opacity-85">
                      Use this if a salon owner invited you to work under an
                      existing Ellyanna salon account.
                    </span>
                  </span>
                </button>
              </div>
            </div>
          ) : null}

          <div className="mt-7 grid gap-3">
            <div className="rounded-2xl border border-slate-200 bg-white px-4 py-4 shadow-sm">
              <div className="text-sm font-semibold text-slate-900">
                Ellyanna Booking
              </div>
              <p className="mt-1 text-sm leading-6 text-slate-600">
                Manage appointments, clients, services, staff schedules, sales,
                checkout, setup, and daily salon operations.
              </p>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white px-4 py-4 shadow-sm">
              <div className="text-sm font-semibold text-slate-900">
                Ellyanna MyBoothShare
              </div>
              <p className="mt-1 text-sm leading-6 text-slate-600">
                Use connected workspace rental tools when you need to list,
                find, book, or manage salon workspace opportunities.
              </p>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white px-4 py-4 shadow-sm">
              <div className="text-sm font-semibold text-slate-900">
                Ellyanna SalonBusinessCPA
              </div>
              <p className="mt-1 text-sm leading-6 text-slate-600">
                Organize accounting, tax, payroll, bookkeeping, GST/HST,
                financial statement, and document checklist work.
              </p>
            </div>
          </div>

          {hasSelectedAccountPath && mode === "new" && !successInfo ? (
            <div className="mt-6 rounded-2xl border border-[#2f3566]/10 bg-[#2f3566]/5 px-4 py-4 text-sm leading-6 text-slate-600">
              {isIndependentProfessional
                ? "Your Ellyanna account is the base account for your booking setup, workspace rentals, and SalonBusinessCPA records."
                : "Your Ellyanna account is the base account for your salon booking setup, workspace listings, and SalonBusinessCPA records."}
            </div>
          ) : null}
        </section>

        <section className="rounded-[28px] border border-slate-200 bg-white px-5 py-5 shadow-[0_20px_60px_rgba(15,23,42,0.12)] md:px-7 md:py-7">
          <div className="space-y-5 px-4 py-5 sm:px-6 sm:py-6">
            {error && (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                {error}
              </div>
            )}

            {successInfo ? (
              <div className="space-y-4">
                {successInfo.mode === "created_salon" ? (
                  <div className="space-y-2 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-4 text-sm text-emerald-800">
                    <p className="text-base font-semibold text-emerald-900">
                      Your salon account has been created.
                    </p>
                    <p>
                      Please check your email to verify your account. After
                      verification, sign in to continue your salon setup.
                    </p>
                    {successInfo.salon_id ? (
                      <p>
                        Your Salon ID:{" "}
                        <span className="font-mono">{successInfo.salon_id}</span>
                      </p>
                    ) : null}
                    {successInfo.salon_name && (
                      <p>
                        Salon name: <span className="font-semibold">{successInfo.salon_name}</span>
                      </p>
                    )}
                    <p>
                      Keep this ID. You and your team will use it when signing in
                      to your salon account.
                    </p>
                    <button
                      type="button"
                      disabled={autoLoginLoading}
                      onClick={() => handleAutoLogin("/setup")}
                      className="mt-2 w-full rounded-xl bg-rose-500 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-rose-600 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {autoLoginLoading
                        ? "Checking access..."
                        : successInfo.verification_required
                        ? "I've verified my email - continue"
                        : "Continue setup and start your 30-day trial"}
                    </button>
                    {successInfo.verification_required ? (
                      <button
                        type="button"
                        onClick={() =>
                          navigate(buildOpsVerifyEmailUrl(email, successInfo.salon_id))
                        }
                        className="w-full rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                      >
                        Verify my email
                      </button>
                    ) : null}
                  </div>
                ) : successInfo.mode === "created_independent_professional" ? (
                  <div className="space-y-3 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-4 text-sm text-emerald-800">
                    <p className="text-base font-semibold text-emerald-900">
                      Your independent professional account has been created.
                    </p>
                    <p>
                      Please check your email to verify your account. After
                      verification, sign in to continue your business setup.
                    </p>
                    {getIndependentProfessionalDisplayId(successInfo) ? (
                      <p>
                        Your Professional ID:{" "}
                        <span className="font-mono">
                          {getIndependentProfessionalDisplayId(successInfo)}
                        </span>
                      </p>
                    ) : null}
                    {successInfo.full_name ? (
                      <p>
                        Account name:{" "}
                        <span className="font-semibold">{successInfo.full_name}</span>
                      </p>
                    ) : null}
                    <p>
                      Keep this ID. You will use it when signing in as an
                      independent professional.
                    </p>
                    {successInfo.verification_required ? (
                      <button
                        type="button"
                        onClick={() => navigate(buildOpsVerifyEmailUrl(email))}
                        className="mt-2 w-full rounded-xl bg-rose-500 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-rose-600"
                      >
                        Verify my email
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => navigate("/setup?mode=independent_professional")}
                        className="mt-2 w-full rounded-xl bg-rose-500 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-rose-600"
                      >
                        Continue to Independent Professional setup
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="space-y-2 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-4 text-sm text-amber-800">
                    <p className="font-semibold">
                      Request sent. Waiting for salon approval.
                    </p>
                    {successInfo.salon_name && (
                      <p>
                        Salon: <span className="font-semibold">{successInfo.salon_name}</span>
                      </p>
                    )}
                    {successInfo.status && <p>Status: {successInfo.status}</p>}
                    <p>We will let you know when an owner or manager approves your request.</p>
                    {successInfo.verification_required ? (
                      <p>Verify your email before your first Ellyanna sign-in.</p>
                    ) : null}
                    {refreshStatusMessage ? (
                      <div className="rounded-xl border border-amber-200 bg-white/80 px-3 py-2 text-xs text-amber-900">
                        {refreshStatusMessage}
                      </div>
                    ) : null}
                    <button
                      type="button"
                      disabled={autoLoginLoading}
                      onClick={handleRefreshAccess}
                      className="mt-2 w-full rounded-xl bg-rose-500 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-rose-600 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {autoLoginLoading ? "Checking approval..." : "Refresh approval status"}
                    </button>
                    {successInfo.verification_required ? (
                      <button
                        type="button"
                        onClick={() =>
                          navigate(
                            buildOpsVerifyEmailUrl(
                              email,
                              successInfo.salon_id || existingSalonId
                            )
                          )
                        }
                        className="w-full rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                      >
                        Verify my email
                      </button>
                    ) : null}
                    <button
                      type="button"
                      onClick={() =>
                        navigate(
                          successInfo.salon_id
                            ? `/login?salonId=${encodeURIComponent(successInfo.salon_id)}`
                            : "/login"
                        )
                      }
                      className="w-full rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                    >
                      Go to login
                    </button>
                  </div>
                )}

                {successInfo.mode !== "created_salon" && (
                  <p className="text-center text-xs text-slate-500">
                    Already have an account?{" "}
                    <button
                      type="button"
                      onClick={() => navigate("/login")}
                      className="font-semibold text-rose-600 hover:underline"
                    >
                      Sign in
                    </button>
                  </p>
                )}
              </div>
            ) : !hasSelectedAccountPath ? (
              <div className="relative overflow-hidden rounded-3xl border border-dashed border-slate-300 bg-slate-50 px-6 py-8 text-center">
                <div className="absolute left-6 top-6 h-16 w-16 rounded-full bg-rose-100 blur-2xl" aria-hidden="true" />
                <div className="absolute bottom-6 right-6 h-20 w-20 rounded-full bg-indigo-100 blur-2xl" aria-hidden="true" />
                <div className="relative mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-white shadow-[0_14px_40px_rgba(47,53,102,0.12)]">
                  <span className="h-3 w-3 animate-pulse rounded-full bg-rose-500" aria-hidden="true" />
                  <span className="mx-1 h-3 w-3 animate-pulse rounded-full bg-[#2f3566] [animation-delay:160ms]" aria-hidden="true" />
                  <span className="h-3 w-3 animate-pulse rounded-full bg-rose-300 [animation-delay:320ms]" aria-hidden="true" />
                </div>
                <h2 className="relative mt-5 text-2xl font-semibold text-slate-900">
                  Choose an account type to continue
                </h2>
                <p className="relative mt-3 text-sm leading-6 text-slate-600">
                  Select Salon Owner, Independent Professional, or Join Existing
                  Salon. The registration form will appear with the right fields
                  for that account.
                </p>
              </div>
            ) : (
              <>
                <div>
                  <h2 className="text-2xl font-semibold text-slate-900">
                    {formTitle}
                  </h2>
                  <p className="mt-2 text-sm leading-6 text-slate-600">
                    {formIntro}
                  </p>
                </div>

                {mode === "existing" ? (
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 text-sm leading-6 text-slate-600">
                    Ask the salon owner or manager for the correct Salon ID
                    before continuing.
                  </div>
                ) : null}

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="text-xs font-semibold uppercase tracking-[0.18em] text-[#2f3566]">
                    Account details
                  </div>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <label htmlFor="ops-register-first-name" className={labelClassName}>First name</label>
                      <input
                        id="ops-register-first-name"
                        type="text"
                        required
                        className={inputClassName}
                        value={firstName}
                        onChange={(e) => {
                          setFirstName(e.target.value);
                          clearFieldError("first_name");
                        }}
                        placeholder="Enter your first name"
                      />
                      {fieldErrors.first_name && (
                        <p className={helperClassName}>{fieldErrors.first_name}</p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="ops-register-last-name" className={labelClassName}>Last name</label>
                      <input
                        id="ops-register-last-name"
                        type="text"
                        required
                        className={inputClassName}
                        value={lastName}
                        onChange={(e) => {
                          setLastName(e.target.value);
                          clearFieldError("last_name");
                        }}
                        placeholder="Enter your last name"
                      />
                      {fieldErrors.last_name && (
                        <p className={helperClassName}>{fieldErrors.last_name}</p>
                      )}
                    </div>
                  </div>

                  <div>
                    <label htmlFor="ops-register-email" className={labelClassName}>Email</label>
                    <input
                      id="ops-register-email"
                      type="email"
                      required
                      className={inputClassName}
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        clearFieldError("email");
                      }}
                      placeholder="you@example.com"
                    />
                    {fieldErrors.email && <p className={helperClassName}>{fieldErrors.email}</p>}
                  </div>

                  <div>
                    <label htmlFor="ops-register-username" className={labelClassName}>Login username</label>
                    <input
                      id="ops-register-username"
                      type="text"
                      className={inputClassName}
                      value={username}
                      onChange={(e) => {
                        setUsername(e.target.value);
                        clearFieldError("username");
                      }}
                      placeholder="Optional username"
                    />
                    <p className="mt-1 text-xs text-slate-500">
                      Optional. You can sign in later with your email or this username.
                    </p>
                    {fieldErrors.username && (
                      <p className={helperClassName}>{fieldErrors.username}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="ops-register-password" className={labelClassName}>Password</label>
                    <input
                      id="ops-register-password"
                      type="password"
                      required
                      className={inputClassName}
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        clearFieldError("password");
                      }}
                      placeholder={
                        mode === "new"
                          ? "At least 8 characters"
                          : "Use your current password"
                      }
                    />
                    {fieldErrors.password && (
                      <p className={helperClassName}>{fieldErrors.password}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="ops-register-confirm-password" className={labelClassName}>Confirm password</label>
                    <input
                      id="ops-register-confirm-password"
                      type="password"
                      required
                      className={inputClassName}
                      value={confirmPassword}
                      onChange={(e) => {
                        setConfirmPassword(e.target.value);
                        clearFieldError("confirm_password");
                      }}
                      placeholder="Re-enter your password"
                    />
                    {fieldErrors.confirm_password && (
                      <p className={helperClassName}>{fieldErrors.confirm_password}</p>
                    )}
                  </div>

                  {mode === "new" && (
                    <div>
                      <label htmlFor="ops-register-business-name" className={labelClassName}>
                        {businessNameLabel}
                      </label>
                      <input
                        id="ops-register-business-name"
                        type="text"
                        className={inputClassName}
                        value={salonName}
                        onChange={(e) => setSalonName(e.target.value)}
                        placeholder={businessNamePlaceholder}
                      />
                    </div>
                  )}

                  {mode === "existing" && (
                    <div>
                      <label htmlFor="ops-register-salon-id" className={labelClassName}>Salon ID</label>
                      <input
                        id="ops-register-salon-id"
                        type="number"
                        min="1"
                        required
                        className={inputClassName}
                        value={existingSalonId}
                        onChange={(e) => {
                          setExistingSalonId(e.target.value);
                          clearFieldError("salon_id");
                        }}
                        placeholder="Enter salon ID"
                      />
                      {fieldErrors.salon_id && (
                        <p className={helperClassName}>{fieldErrors.salon_id}</p>
                      )}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full rounded-xl bg-rose-500 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-rose-600 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {loading ? loadingLabel : submitLabel}
                  </button>
                </form>

                <p className="text-center text-xs text-slate-500">
                  Already have an account?{" "}
                  <button
                    type="button"
                    onClick={() => navigate("/login")}
                    className="font-semibold text-rose-600 hover:underline"
                  >
                    Sign in
                  </button>
                </p>
              </>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

export default function Register() {
  const navigate = useNavigate();
  const location = useLocation();
  const runtimeBrand = useBrand();
  const { login } = useAuth();
  const isMarketplaceHost = runtimeBrand.isMarketplaceHost;

  const [mode, setMode] = useState("new");
  const [accountType, setAccountType] = useState(() =>
    getInitialOpsAccountType(location.search)
  );
  const [selectedOpsRegistrationPath, setSelectedOpsRegistrationPath] = useState(() =>
    getInitialOpsRegistrationPath(location.search)
  );
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [salonName, setSalonName] = useState("");
  const [existingSalonId, setExistingSalonId] = useState("");
  const [loading, setLoading] = useState(false);
  const [autoLoginLoading, setAutoLoginLoading] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [successInfo, setSuccessInfo] = useState(null);
  const [refreshStatusMessage, setRefreshStatusMessage] = useState("");
  const [selectedRegistrationPath, setSelectedRegistrationPath] = useState(() =>
    getInitialMarketplaceRegistrationPath(location.search)
  );

  useEffect(() => {
    setSelectedRegistrationPath(getInitialMarketplaceRegistrationPath(location.search));
    setAccountType(getInitialOpsAccountType(location.search));
    const nextOpsPath = getInitialOpsRegistrationPath(location.search);
    setSelectedOpsRegistrationPath(nextOpsPath);
    if (nextOpsPath === "existing") {
      setMode("existing");
    } else if (nextOpsPath) {
      setMode("new");
    }
  }, [location.search]);

  const [marketplaceForm, setMarketplaceForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    username: "",
    password: "",
    confirmPassword: "",
    displayName: "",
    businessName: "",
    phone: "",
    licenseType: "",
    instagramHandle: "",
    defaultWorkspaceOperatorMode: "independent_owner",
    ellyannaBookingRequiredAccepted: false,
  });

  const clearFieldError = (field) => {
    setFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const handleModeChange = (nextMode) => {
    setMode(nextMode);
    setError("");
    setFieldErrors({});
    setSuccessInfo(null);
    setRefreshStatusMessage("");
    if (nextMode === "new") {
      setExistingSalonId("");
    } else {
      setSalonName("");
      setAccountType("owner");
    }
  };

  const handleOpsSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setFieldErrors({});

    const nextFieldErrors = {};
    const trimmedFirstName = firstName.trim();
    const trimmedLastName = lastName.trim();
    const trimmedEmail = email.trim();
    const trimmedUsername = username.trim();
    const trimmedSalonName = salonName.trim();
    const trimmedSalonId = existingSalonId.trim();

    if (!trimmedFirstName) nextFieldErrors.first_name = "First name is required.";
    if (!trimmedLastName) nextFieldErrors.last_name = "Last name is required.";
    if (!trimmedEmail) nextFieldErrors.email = "Email is required.";
    if (trimmedUsername && !/^[a-zA-Z0-9_-]+$/.test(trimmedUsername)) {
      nextFieldErrors.username =
        "Username can only use letters, numbers, underscores, or hyphens.";
    }
    if (mode === "new" && password.length < 8) {
      nextFieldErrors.password = "Password must be at least 8 characters.";
    }
    if (password !== confirmPassword) {
      nextFieldErrors.confirm_password = "Passwords do not match.";
    }
    if (mode === "existing" && !trimmedSalonId) {
      nextFieldErrors.salon_id = "Salon ID is required.";
    }

    if (Object.keys(nextFieldErrors).length > 0) {
      setFieldErrors(nextFieldErrors);
      setError("Please review the highlighted fields.");
      return;
    }

    let salonIdValue = null;
    if (mode === "existing") {
      const parsedId = Number(trimmedSalonId);
      if (!parsedId || Number.isNaN(parsedId)) {
        setFieldErrors((prev) => ({
          ...prev,
          salon_id: "Salon ID must be a positive number.",
        }));
        setError("Salon ID must be a positive number.");
        return;
      }
      salonIdValue = parsedId;
    }

    setLoading(true);
    setSuccessInfo(null);
    try {
      const payload = {
        first_name: trimmedFirstName,
        last_name: trimmedLastName,
        email: trimmedEmail,
        username: trimmedUsername || null,
        password,
        account_type: mode === "existing" ? "technician" : accountType,
        salon_id: salonIdValue,
      };
      if (mode === "new" && trimmedSalonName) {
        payload.salon_name = trimmedSalonName;
      }

      const signupRes = await fetch(`${API}/signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const signupData = await signupRes.json().catch(() => ({}));
      if (!signupRes.ok) {
        if (signupData?.detail === "USERNAME_TAKEN") {
          setFieldErrors({ username: "That username is already in use." });
          setError("Please review the highlighted fields.");
          return;
        }
        if (signupData?.detail === "USERNAME_INVALID") {
          setFieldErrors({
            username: "Username can only use letters, numbers, underscores, or hyphens.",
          });
          setError("Please review the highlighted fields.");
          return;
        }
        setError(
          signupData?.detail ||
            "Registration failed. Please check your details and try again."
        );
        return;
      }

      const fallbackFullName = `${trimmedFirstName} ${trimmedLastName}`.trim();
      setSuccessInfo({
        ...signupData,
        full_name: (signupData?.full_name || fallbackFullName).trim(),
      });
    } catch (err) {
      console.error("Registration error:", err);
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleMarketplaceSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setFieldErrors({});

    const nextFieldErrors = {};
    const trimmedFirstName = marketplaceForm.firstName.trim();
    const trimmedLastName = marketplaceForm.lastName.trim();
    const trimmedEmail = marketplaceForm.email.trim();

    if (!trimmedFirstName) nextFieldErrors.first_name = "First name is required.";
    if (!trimmedLastName) nextFieldErrors.last_name = "Last name is required.";
    if (!trimmedEmail) nextFieldErrors.email = "Email is required.";
    if (marketplaceForm.username.trim() && !/^[a-zA-Z0-9_-]+$/.test(marketplaceForm.username.trim())) {
      nextFieldErrors.username =
        "Username can only use letters, numbers, underscores, or hyphens.";
    }
    if (marketplaceForm.password.length < 8) {
      nextFieldErrors.password = "Password must be at least 8 characters.";
    }
    if (marketplaceForm.password !== marketplaceForm.confirmPassword) {
      nextFieldErrors.confirm_password = "Passwords do not match.";
    }
    if (!marketplaceForm.ellyannaBookingRequiredAccepted) {
      nextFieldErrors.ellyanna_booking_required =
        "You must agree to use Ellyanna Booking to use MyBoothShare.";
    }

    if (Object.keys(nextFieldErrors).length > 0) {
      setFieldErrors(nextFieldErrors);
      setError("Please review the highlighted fields.");
      return;
    }

    setLoading(true);
    try {
      const data = await marketplaceSignup({
        first_name: trimmedFirstName,
        last_name: trimmedLastName,
        email: trimmedEmail,
        username: marketplaceForm.username.trim() || null,
        password: marketplaceForm.password,
        display_name: marketplaceForm.displayName || null,
        business_name: marketplaceForm.businessName || null,
        phone: marketplaceForm.phone || null,
        license_type: marketplaceForm.licenseType || null,
        instagram_handle: marketplaceForm.instagramHandle || null,
        default_workspace_operator_mode:
          marketplaceForm.defaultWorkspaceOperatorMode,
        ellyanna_booking_required_accepted: true,
      });
      if (data?.access_token) {
        login(data.access_token, data);
        navigate("/account/profile", { replace: true });
        return;
      }
      if (data?.verification_required) {
        navigate(
          `/marketplace/verify-email?email=${encodeURIComponent(trimmedEmail)}`,
          { replace: true }
        );
        return;
      }
      navigate("/login", { replace: true });
    } catch (err) {
      console.error("Marketplace registration error:", err);
      if (err?.message === "USERNAME_TAKEN") {
        setFieldErrors({ username: "That username is already in use." });
        setError("Please review the highlighted fields.");
      } else if (err?.message === "USERNAME_INVALID") {
        setFieldErrors({
          username: "Username can only use letters, numbers, underscores, or hyphens.",
        });
        setError("Please review the highlighted fields.");
      } else {
        setError(err.message || "Unable to create your account.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAutoLogin = async (redirectPath = null) => {
    if (!successInfo || successInfo.mode !== "created_salon") {
      return;
    }
    setError("");
    setRefreshStatusMessage("");
    setAutoLoginLoading(true);

    try {
      const loginData = await loginApi(successInfo.salon_id, email.trim(), password);
      const normalizedRole = (loginData.role || successInfo.role || "").toLowerCase();

      if (loginData.access_token) {
        login(loginData.access_token, {
          ...loginData,
          role: normalizedRole,
        });

        if (typeof window !== "undefined") {
          window.localStorage.setItem("onboarding_completed", "false");
          notifyOnboardingStatusChange(false);
        }
      }

      let destination = redirectPath;
      if (!destination) {
        if (normalizedRole === "owner") destination = "/setup";
        else if (normalizedRole === "technician") destination = "/technician/setup";
        else destination = "/manager/schedule";
      }
      navigate(destination, { replace: true });
    } catch (err) {
      console.error("Auto-login / continue error:", err);
      const detail = err?.payload?.detail ?? err?.response?.data?.detail;
      const detailCode =
        detail && typeof detail === "object"
          ? detail.code || detail.detail || null
          : typeof detail === "string"
          ? detail
          : null;
      if (detailCode === "EMAIL_VERIFICATION_REQUIRED") {
        navigate(buildOpsVerifyEmailUrl(email, successInfo.salon_id), {
          replace: true,
        });
        return;
      }
      setError("We couldn't continue your sign-in. Please try again from the login page.");
    } finally {
      setAutoLoginLoading(false);
    }
  };

  const handleRefreshAccess = async () => {
    if (!successInfo?.salon_id && !existingSalonId.trim()) {
      return;
    }
    setError("");
    setRefreshStatusMessage("");
    setAutoLoginLoading(true);
    try {
      const resolvedSalonId = successInfo?.salon_id || existingSalonId.trim();
      const loginData = await loginApi(resolvedSalonId, email.trim(), password);
      const normalizedRole = (loginData.role || successInfo?.role || "").toLowerCase();

      if (loginData.access_token) {
        login(loginData.access_token, {
          ...loginData,
          role: normalizedRole,
        });
      }

      let destination = "/manager/schedule";
      if (normalizedRole === "technician") destination = "/technician/setup";
      navigate(destination, { replace: true });
    } catch (err) {
      console.error("Refresh approval error:", err);
      const detail = err?.payload?.detail ?? err?.response?.data?.detail;
      const detailCode =
        detail && typeof detail === "object"
          ? detail.code || detail.detail || null
          : typeof detail === "string"
          ? detail
          : null;
      if (detailCode === "EMAIL_VERIFICATION_REQUIRED") {
        navigate(
          buildOpsVerifyEmailUrl(email, successInfo?.salon_id || existingSalonId),
          { replace: true }
        );
        return;
      }
      setRefreshStatusMessage(
        OPS_LOGIN_ERROR_MESSAGES[detailCode] ||
          "Your request is still pending. Try again after the salon owner or manager approves it."
      );
    } finally {
      setAutoLoginLoading(false);
    }
  };

  if (isMarketplaceHost) {
    return (
      <MarketplaceRegister
        error={error}
        fieldErrors={fieldErrors}
        form={marketplaceForm}
        loading={loading}
        onChange={(field, value) => {
          setMarketplaceForm((prev) => ({ ...prev, [field]: value }));
          const fieldMap = {
            firstName: "first_name",
            lastName: "last_name",
            email: "email",
            username: "username",
            password: "password",
            confirmPassword: "confirm_password",
          };
          const mappedField = fieldMap[field];
          if (mappedField && fieldErrors[mappedField]) {
            clearFieldError(mappedField);
          }
        }}
        opsLoginUrl={runtimeBrand.opsLoginUrl || "https://ellyanna.com/login"}
        opsRegisterUrl={buildOpsRegisterUrl(runtimeBrand)}
        onSubmit={handleMarketplaceSubmit}
        selectedRegistrationPath={selectedRegistrationPath}
        setSelectedRegistrationPath={setSelectedRegistrationPath}
      />
    );
  }

  return (
    <OpsRegister
      accountType={accountType}
      autoLoginLoading={autoLoginLoading}
      clearFieldError={clearFieldError}
      confirmPassword={confirmPassword}
      email={email}
      error={error}
      existingSalonId={existingSalonId}
      fieldErrors={fieldErrors}
      firstName={firstName}
      handleAutoLogin={handleAutoLogin}
      handleRefreshAccess={handleRefreshAccess}
      handleModeChange={handleModeChange}
      handleSubmit={handleOpsSubmit}
      lastName={lastName}
      loading={loading}
      mode={mode}
      navigate={navigate}
      password={password}
      refreshStatusMessage={refreshStatusMessage}
      salonName={salonName}
      selectedAccountPath={selectedOpsRegistrationPath}
      setSelectedAccountPath={setSelectedOpsRegistrationPath}
      setConfirmPassword={setConfirmPassword}
      setEmail={setEmail}
      setExistingSalonId={setExistingSalonId}
      setFirstName={setFirstName}
      setLastName={setLastName}
      setPassword={setPassword}
      setAccountType={setAccountType}
      setUsername={setUsername}
      setSalonName={setSalonName}
      successInfo={successInfo}
      username={username}
    />
  );
}
